import { Stack, Switch, Theme, useTheme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { MessageSquare, Phone } from 'lucide-react';
import { ReactNode, useEffect, useRef } from 'react';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { SettingsSection, SettingsSectionRow } from 'src/components/SettingsSection';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { hasPlatformPartnerAccess, useGroupRules } from 'src/graphql';
import { usePlatformWorkflowsByTrigger } from 'src/graphql/entities/workflow/hooks/use-platform-workflows-by-trigger';
import { type WorkflowByTriggerOption } from 'src/graphql/entities/workflow/hooks/use-workflows-by-trigger';
import {
  ORDERED_WORKFLOW_TRIGGER_TYPES,
  WORKFLOW_TRIGGER_LABELS,
  WorkflowTriggerType,
} from 'src/graphql/entities/workflow/workflow.trigger';
import { useWorkflowEmergencyOverride } from 'src/pages/Settings/preferences/hooks/use-workflow-emergency-override';
import { type WorkflowEmergencyOverrideConfig } from 'src/pages/Settings/preferences/hooks/workflow-emergency-override.constants';
import { useGlobalStore } from 'src/store';

const MASTER_LABEL = 'Override all triggers';

/**
 * Marks the chosen option with `sys.secondary` instead of the portal's usual teal, per the
 * design reference: mid-incident "which workflow is pinned" has to survive a glance. Scoped
 * through `menuSx` so the shared `SelectMenu` keeps its colours everywhere else.
 *
 * `data-option-value` / `data-check` are attributes the component emits, not test ids.
 */
function workflowMenuSx(workflowId: string | undefined): SystemStyleObject<Theme> {
  if (!workflowId) return {};

  return {
    [`& [data-option-value="${CSS.escape(workflowId)}"]`]: {
      color: 'custom.OV.sys.secondary',
      fontWeight: 600,
      '& [data-check]': { color: 'custom.OV.sys.secondary' },
    },
  };
}

/** Wide enough for the disabled-option sentence on one line, never wider than the screen. */
const WIDE_MENU_WIDTH = 'min(32rem, calc(100vw - 2rem))';

/** Past this a workflow name wraps at the trigger's own width. */
const LONG_NAME_LENGTH = 28;

/**
 * Restates `SelectMenuTrigger`'s own floor as a plain length.
 *
 * Its default is `min(9.75rem, 100%)`, and the percentage collapses to zero while the browser
 * measures this shrink-to-fit row — so the row is sized for the label's natural width, the
 * trigger then lays out at the full floor, and the switch beside it is pushed past the row's
 * right edge by the difference. That is what takes these switches out of line with every other
 * toggle on the page. A plain length is measurable, so the row reserves the right width.
 */
const TRIGGER_MIN_WIDTH = '9.75rem';

function needsWideMenu(workflows: WorkflowByTriggerOption[]): boolean {
  return workflows.some(
    (workflow) => !workflow.hasActiveVersion || workflow.name.length > LONG_NAME_LENGTH,
  );
}

const TRIGGER_ICONS: Record<WorkflowTriggerType, ReactNode> = {
  [WorkflowTriggerType.InboundPhoneCall]: <Phone />,
  [WorkflowTriggerType.InboundSms]: <MessageSquare />,
};

interface TriggerOverrideRowProps {
  triggerType: WorkflowTriggerType;
  workflowId?: string;
  enabled: boolean;
  /** The master switch is on, so this trigger is overridden whatever its own flag says. */
  coveredByMaster: boolean;
  workflows: WorkflowByTriggerOption[];
  workflowsLoading: boolean;
  disabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onWorkflowChange: (workflowId: string) => void;
}

const TriggerOverrideRow = ({
  triggerType,
  workflowId,
  enabled,
  coveredByMaster,
  workflows,
  workflowsLoading,
  disabled,
  onEnabledChange,
  onWorkflowChange,
}: TriggerOverrideRowProps) => {
  const theme = useTheme();
  const label = WORKFLOW_TRIGGER_LABELS[triggerType];
  const options = buildOptions(workflows, workflowId);
  const hasSelection = !!workflowId;
  const hasRunnableWorkflow = workflows.some(
    (workflow) => workflow.workflowId === workflowId && workflow.hasActiveVersion,
  );
  const hasAnyRunnable = workflows.some((workflow) => workflow.hasActiveVersion);

  // Master covers a trigger only if it has something to force — the backend reads "on with
  // no workflow" as off.
  const isOn = enabled && hasSelection;
  const heldByMaster = coveredByMaster && hasSelection;
  // Master on but nothing selected: looks identical to "covered", so colour it.
  const needsWorkflow = coveredByMaster && !hasSelection;

  return (
    <SettingsSectionRow
      icon={TRIGGER_ICONS[triggerType]}
      title={label}
      secondary={triggerSecondary({
        label,
        coveredByMaster: heldByMaster,
        needsWorkflow,
        workflowsLoading,
        hasWorkflows: hasAnyRunnable,
        canEnable: isOn || hasRunnableWorkflow,
      })}
      mobileControlLayout="below"
      control={
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <SelectMenu
            label={`${label} workflow`}
            placeholder="Select a workflow"
            options={options}
            value={workflowId ?? null}
            onChange={onWorkflowChange}
            disabled={disabled || !workflows.length}
            search
            searchPlaceholder="Search workflows…"
            emptyText="No platform workflow for this trigger"
            // Widen past the trigger so a disabled row's reason reads on one line; capped
            // against the viewport so it still fits a phone.
            menuWidth={needsWideMenu(workflows) ? WIDE_MENU_WIDTH : 'trigger'}
            menuSx={workflowMenuSx(workflowId)}
            sx={{
              minWidth: TRIGGER_MIN_WIDTH,
              ...(needsWorkflow
                ? {
                    borderColor: theme.palette.custom.OV.sys.error,
                    '&:hover': { borderColor: theme.palette.custom.OV.sys.error },
                  }
                : {}),
            }}
          />
          <Switch
            checked={isOn}
            // Held while master covers this trigger. Otherwise turning it *on* needs a
            // runnable workflow, since the backend now stops rather than degrades; turning it
            // *off* stays open, or a workflow deleted afterwards would trap it on.
            disabled={disabled || heldByMaster || (!isOn && !hasRunnableWorkflow)}
            onChange={(event) => onEnabledChange(event.target.checked)}
            inputProps={{ 'aria-label': `Override ${label}` }}
          />
        </Stack>
      }
    />
  );
};

/**
 * Keeps a configured-but-unselectable workflow visible, un-pickable. Without it the select
 * falls back to its placeholder while the config still names that workflow — a switch reading
 * "on" above an apparently empty selection.
 */
/**
 * Forgets a configured workflow that can no longer be forced, so the row reads honestly as
 * "nothing selected" rather than letting master appear to cover a trigger the backend refuses.
 *
 * Only fires on a non-empty workflow list with no runnable match — positive evidence the data
 * arrived. An empty list is ambiguous (none exist, or the query failed) and costs nothing to
 * leave alone: the switch is already disabled.
 */
function useClearUnrunnableSelections({
  config,
  workflowsByTrigger,
  ready,
  clearTriggerWorkflow,
}: {
  config: WorkflowEmergencyOverrideConfig;
  workflowsByTrigger: Record<WorkflowTriggerType, WorkflowByTriggerOption[]>;
  ready: boolean;
  clearTriggerWorkflow: (triggerType: WorkflowTriggerType) => Promise<void>;
}): void {
  // Cleared ids, so a failed write is not retried on every render.
  const attempted = useRef(new Set<string>());

  useEffect(() => {
    if (!ready) return;

    ORDERED_WORKFLOW_TRIGGER_TYPES.forEach((triggerType) => {
      const workflowId = config.triggers?.[triggerType]?.workflowId;
      const workflows = workflowsByTrigger[triggerType] ?? [];

      if (!workflowId || !workflows.length) return;

      const runnable = workflows.some(
        (workflow) => workflow.workflowId === workflowId && workflow.hasActiveVersion,
      );
      const key = `${triggerType}:${workflowId}`;

      if (runnable || attempted.current.has(key)) return;

      attempted.current.add(key);
      void clearTriggerWorkflow(triggerType);
    });
  }, [config, workflowsByTrigger, ready, clearTriggerWorkflow]);
}

const WorkflowEmergencyOverrideContent = () => {
  const {
    config,
    loading,
    isSaving,
    setMasterEnabled,
    setTriggerEnabled,
    setTriggerWorkflow,
    clearTriggerWorkflow,
  } = useWorkflowEmergencyOverride();
  const { workflowsByTrigger, loading: workflowsLoading } = usePlatformWorkflowsByTrigger();

  useClearUnrunnableSelections({
    config,
    workflowsByTrigger,
    ready: !loading && !workflowsLoading && !isSaving,
    clearTriggerWorkflow,
  });

  if (loading) return null;

  const masterEnabled = config.master === true;

  return (
    <SettingsSection
      title="Emergency workflow override"
      description="Break-glass incident control. While a trigger is overridden, it runs the selected platform workflow for every partner instead of the partner's own."
    >
      <SettingsSectionRow
        title={MASTER_LABEL}
        secondary="The kill switch: turns every trigger's override on at once, so a partner-wide incident is one click. Triggers with a workflow selected below run that workflow; the rest fall back to whichever platform workflow matches. Turn it back off once the incident is over."
        control={
          <Switch
            checked={masterEnabled}
            disabled={isSaving}
            onChange={(event) => void setMasterEnabled(event.target.checked)}
            inputProps={{ 'aria-label': MASTER_LABEL }}
          />
        }
      />

      {ORDERED_WORKFLOW_TRIGGER_TYPES.map((triggerType) => (
        <TriggerOverrideRow
          key={triggerType}
          triggerType={triggerType}
          workflowId={config.triggers?.[triggerType]?.workflowId}
          enabled={masterEnabled || config.triggers?.[triggerType]?.enabled === true}
          coveredByMaster={masterEnabled}
          workflows={workflowsByTrigger[triggerType] ?? []}
          workflowsLoading={workflowsLoading}
          disabled={isSaving || workflowsLoading}
          onEnabledChange={(enabled) => void setTriggerEnabled(triggerType, enabled)}
          onWorkflowChange={(selectedId) => void setTriggerWorkflow(triggerType, selectedId)}
        />
      ))}
    </SettingsSection>
  );
};

function buildOptions(
  workflows: WorkflowByTriggerOption[],
  workflowId: string | undefined,
): SelectMenuOption[] {
  // Kept on the list, disabled and captioned, rather than dropped — otherwise the workflow
  // just vanishes with no hint of what to do about it.
  const options: SelectMenuOption[] = workflows.map((workflow) => ({
    value: workflow.workflowId,
    title: workflow.name,
    ...(workflow.hasActiveVersion
      ? {}
      : {
          disabled: true,
          description: 'No active version — activate one before forcing this workflow.',
        }),
  }));

  if (workflowId && !workflows.some((workflow) => workflow.workflowId === workflowId)) {
    options.push({
      value: workflowId,
      title: 'Selected workflow is no longer active',
      disabled: true,
    });
  }

  return options;
}

function triggerSecondary({
  label,
  coveredByMaster,
  needsWorkflow,
  workflowsLoading,
  hasWorkflows,
  canEnable,
}: {
  label: string;
  coveredByMaster: boolean;
  /** Master is on, but this trigger has nothing selected and so is not being forced. */
  needsWorkflow: boolean;
  workflowsLoading: boolean;
  hasWorkflows: boolean;
  /** Whether the switch can be turned on: it needs a workflow that can actually run. */
  canEnable: boolean;
}): string {
  if (workflowsLoading) {
    return 'Loading platform workflows…';
  }

  if (!hasWorkflows) {
    return `No platform workflow is active for ${label.toLowerCase()}, so there is nothing to force yet.`;
  }

  if (needsWorkflow) {
    return 'The master switch is on, but nothing is selected here — this trigger is not being forced. Pick a workflow to include it.';
  }

  if (!canEnable) {
    return 'Pick a workflow with an active version before turning this on.';
  }

  if (coveredByMaster) {
    return 'Held on by the master switch above. Turn that off to control this trigger on its own.';
  }

  return 'Forces every partner onto the selected workflow for this trigger, over their own. Only new executions are affected — anything already in flight finishes on the workflow it started with.';
}

/**
 * Settings → Preferences control for `workflow_emergency_override`.
 *
 * Rendered only with the platform partner selected and `access_to_platform_partner` held: the
 * backend reads the setting from the `partner_id = 0` row alone, so a toggle shown anywhere
 * else would look live and do nothing.
 */
export const WorkflowEmergencyOverridePreferences = () => {
  const { groupRules } = useGroupRules();
  const partnerId = useGlobalStore.use.selectedPartnerId();

  if (partnerId !== PLATFORM_PARTNER_ID || !hasPlatformPartnerAccess(groupRules)) {
    return null;
  }

  return <WorkflowEmergencyOverrideContent />;
};
